import React, { useState, useCallback } from 'react';
import {
  Drawer,
  Button,
  Space,
  Table,
  Typography,
  Popconfirm,
  Modal,
  Form,
  Input,
  Avatar,
  message,
  Tooltip,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  PictureOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { SourceCharacter, SourceCharacterCheckpoint } from '../../types';
import { sourceStoriesApi, CreateSourceCharacterCheckpointRequest } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate } from '../../utils';
import CheckpointImageModal from './CheckpointImageModal';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface CharacterCheckpointsDrawerProps {
  visible: boolean;
  character: SourceCharacter;
  storyId: string;
  onClose: () => void;
}

const CharacterCheckpointsDrawer: React.FC<CharacterCheckpointsDrawerProps> = ({
  visible,
  character,
  storyId,
  onClose,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] = useState<SourceCharacterCheckpoint | null>(null);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<SourceCharacterCheckpoint | null>(null);
  const [generatingIds, setGeneratingIds] = useState<string[]>([]);
  const [form] = Form.useForm();

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch checkpoints
  const {
    data: checkpointsData,
    loading: checkpointsLoading,
    refresh: refreshCheckpoints,
  } = useApi(
    useCallback(
      () => (visible ? sourceStoriesApi.getSourceCharacterCheckpoints(storyId, character.id) : Promise.resolve({ data: [], totalRecord: 0 })),
      [storyId, character.id, visible]
    ),
    { immediate: visible }
  );

  const handleCreate = () => {
    setEditingCheckpoint(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (checkpoint: SourceCharacterCheckpoint) => {
    setEditingCheckpoint(checkpoint);
    form.setFieldsValue(checkpoint);
    setIsModalVisible(true);
  };

  const handleDelete = async (checkpoint: SourceCharacterCheckpoint) => {
    await executeAsync(
      () => sourceStoriesApi.deleteSourceCharacterCheckpoint(storyId, character.id, checkpoint.id),
      { successMessage: 'Checkpoint deleted successfully!' }
    );
    refreshCheckpoints();
  };

  const handleManageImages = (checkpoint: SourceCharacterCheckpoint) => {
    setSelectedCheckpoint(checkpoint);
    setIsImageModalVisible(true);
  };

  const handleQuickGenerate = async (checkpoint: SourceCharacterCheckpoint) => {
    setGeneratingIds(prev => [...prev, checkpoint.id]);
    const msgKey = `quick-gen-${checkpoint.id}`;
    message.loading({ content: `Đang khởi tạo prompt cho ${checkpoint.name}...`, key: msgKey });
    
    try {
      // 1. Generate visual prompt (using empty additional info)
      const promptResult = await sourceStoriesApi.generateCharacterVisualPrompt(storyId, character.id, '', checkpoint.id);
      const prompt = promptResult.data.prompt;

      // 2. Update checkpoint visual prompt to DB
      await sourceStoriesApi.updateSourceCharacterCheckpoint(storyId, character.id, checkpoint.id, {
        visualPrompt: prompt
      });

      // 3. Generate image
      message.loading({ content: `Đang tạo ảnh AI cho ${checkpoint.name}...`, key: msgKey });
      await sourceStoriesApi.generateCheckpointImage(storyId, checkpoint.id, prompt);
      
      message.success({ content: `Đã tạo xong ảnh cho Checkpoint: ${checkpoint.name}`, key: msgKey });
      refreshCheckpoints();
    } catch (error: any) {
      message.error({ content: `Lỗi khi tạo ảnh nhanh: ${error.message}`, key: msgKey });
    } finally {
      setGeneratingIds(prev => prev.filter(id => id !== checkpoint.id));
    }
  };

  const handleSubmit = async (values: any) => {
    if (editingCheckpoint) {
      await executeAsync(
        () => sourceStoriesApi.updateSourceCharacterCheckpoint(storyId, character.id, editingCheckpoint.id, values),
        { successMessage: 'Checkpoint updated successfully!' }
      );
    } else {
      await executeAsync(
        () => sourceStoriesApi.createSourceCharacterCheckpoint(storyId, character.id, values as CreateSourceCharacterCheckpointRequest),
        { successMessage: 'Checkpoint created successfully!' }
      );
    }
    setIsModalVisible(false);
    refreshCheckpoints();
  };

  const columns = [
    {
      title: 'Avatar',
      dataIndex: 'avatarUrl',
      key: 'avatar',
      width: 80,
      render: (url: string) => <Avatar src={url} shape="square" size={48} icon={<PictureOutlined />} />,
    },
    {
      title: 'Checkpoint Info',
      key: 'info',
      render: (_: any, record: SourceCharacterCheckpoint) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }} ellipsis={{ tooltip: record.description }}>
            {record.description || 'No description'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_: any, record: SourceCharacterCheckpoint) => (
        <Space>
          <Tooltip title="Tạo nhanh ảnh (AI Prompt + Image)">
            <Button
              type="text"
              icon={<ThunderboltOutlined style={{ color: '#faad14' }} />}
              onClick={() => handleQuickGenerate(record)}
              loading={generatingIds.includes(record.id)}
            />
          </Tooltip>
          <Tooltip title="Manage Images & Details">
            <Button
              type="text"
              icon={<PictureOutlined />}
              onClick={() => handleManageImages(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Info">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this checkpoint?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      title={
        <Space>
          <HistoryOutlined />
          <span>Character Checkpoints: {character.name}</span>
        </Space>
      }
      placement="right"
      width={850}
      onClose={onClose}
      open={visible}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Add Checkpoint
        </Button>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          Checkpoints represent different visual stages or versions of a character throughout the story.
        </Text>
      </div>

      <Table
        columns={columns as any}
        dataSource={checkpointsData?.data || []}
        rowKey="id"
        loading={checkpointsLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{
            emptyText: <Empty description="No checkpoints found. Add one to manage different versions of this character." />
        }}
      />

      <Modal
        title={editingCheckpoint ? 'Edit Checkpoint' : 'Add New Checkpoint'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Checkpoint Name"
            rules={[{ required: true, message: 'Please enter checkpoint name' }]}
          >
            <Input placeholder="e.g., Initial, 5 years later, Battle damaged..." />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={4} placeholder="Describe what changed in this version..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading}
              >
                {editingCheckpoint ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {selectedCheckpoint && (
        <CheckpointImageModal
          visible={isImageModalVisible}
          character={character}
          checkpoint={selectedCheckpoint}
          storyId={storyId}
          onClose={() => {
            setIsImageModalVisible(false);
            setSelectedCheckpoint(null);
            refreshCheckpoints();
          }}
        />
      )}
    </Drawer>
  );
};

export default CharacterCheckpointsDrawer;
